import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, LogOut, Package, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, authHeader, formatApiError, money } from "@/lib/api";
import Stars from "@/components/Stars";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATEGORIES = ["Ceramics", "Leather", "Candles", "Deskware"];
const ORDER_STATUSES = ["processing", "shipped", "delivered", "cancelled"];
const EMPTY_PRODUCT = { name: "", category: "Ceramics", price: "", stock: 10, image: "", description: "", materials: "", featured: false };

const inputCls =
  "w-full border border-white/15 bg-obsidian px-3.5 py-2.5 font-mono text-xs tracking-wider text-bone outline-none transition-colors placeholder:text-faint focus:border-vermilion";

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-white/10 bg-onyx p-6" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">{label}</p>
      <p className="mt-3 font-display text-4xl font-light text-bone">{value}</p>
    </div>
  );
}

function ProductDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial ? { ...EMPTY_PRODUCT, ...initial } : EMPTY_PRODUCT);
  }, [initial, open]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock, 10) || 0 };
    try {
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, payload, authHeader());
      else await api.post("/admin/products", payload, authHeader());
      toast.success(initial?.id ? "Object updated" : "Object added to the catalog");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-white/10 bg-onyx text-bone" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">{initial?.id ? "Edit Object" : "New Object"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="flex flex-col gap-3 pt-2">
          <input required placeholder="NAME" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category-select" className={inputCls}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input required type="number" step="0.01" min="0" placeholder="PRICE USD" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" min="0" placeholder="STOCK" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input" className={inputCls} />
            <label className="flex cursor-pointer items-center gap-3 border border-white/15 px-3.5 font-mono text-xs tracking-wider text-smoke">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="product-featured-checkbox" className="accent-[#FF4500]" />
              FEATURED
            </label>
          </div>
          <input placeholder="IMAGE URL (https://…)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="product-image-input" className={inputCls} />
          <input placeholder="MATERIALS" value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} data-testid="product-materials-input" className={inputCls} />
          <textarea rows={3} placeholder="DESCRIPTION" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" className={`${inputCls} resize-none`} />
          <button type="submit" disabled={saving} data-testid="product-save-btn" className="mt-1 flex items-center justify-center gap-2 bg-vermilion py-3.5 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark disabled:opacity-40">
            {saving ? <Loader2 className="animate-spin" size={14} /> : initial?.id ? "Save Changes" : "Add to Catalog"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProductsTab({ products, reload }) {
  const [dialog, setDialog] = useState({ open: false, product: null });

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the catalog?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`, authHeader());
      toast.success("Object removed");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint">{products.length} objects in the catalog</p>
        <button onClick={() => setDialog({ open: true, product: null })} data-testid="admin-add-product-btn" className="flex items-center gap-2 bg-vermilion px-5 py-3 font-mono text-xs uppercase tracking-[0.25em] text-obsidian transition-colors hover:bg-vermilion-dark">
          <Plus size={14} /> New Object
        </button>
      </div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
              <th className="px-4 py-3">Object</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Featured</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]" data-testid={`admin-product-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-10 w-8 border border-white/10 object-cover" />
                    <span className="font-light text-bone">{p.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-smoke">{p.category}</td>
                <td className="px-4 py-3 font-mono text-xs text-smoke">{money(p.price)}</td>
                <td className="px-4 py-3 font-mono text-xs text-smoke">{p.stock}</td>
                <td className="px-4 py-3">{p.featured ? <Star size={13} className="fill-brass text-brass" /> : <span className="text-faint">—</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setDialog({ open: true, product: p })} data-testid={`admin-edit-product-${p.id}`} aria-label="Edit" className="border border-white/15 p-2 text-smoke transition-colors hover:border-brass hover:text-brass">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`admin-delete-product-${p.id}`} aria-label="Delete" className="border border-white/15 p-2 text-smoke transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ProductDialog open={dialog.open} initial={dialog.product} onClose={() => setDialog({ open: false, product: null })} onSaved={reload} />
    </div>
  );
}

function OrdersTab({ orders, reload }) {
  const setStatus = async (o, status) => {
    try {
      await api.put(`/admin/orders/${o.id}/status`, { status }, authHeader());
      toast.success(`${o.code} → ${status}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {orders.length === 0 && <p className="py-16 text-center font-display text-2xl italic text-faint">No orders yet.</p>}
      {orders.map((o) => (
        <div key={o.id} className="border border-white/10 bg-onyx p-5" data-testid={`admin-order-${o.code}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-display text-2xl text-bone">{o.code}</p>
              <p className="mt-1 font-mono text-[11px] tracking-wider text-faint">{o.name} — {o.email}</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono text-sm text-bone">{money(o.total)}</span>
              <span className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${o.payment_status === "paid" ? "border-brass/40 text-brass" : "border-white/15 text-faint"}`}>
                {o.payment_status}
              </span>
              <select
                value={ORDER_STATUSES.includes(o.status) ? o.status : "processing"}
                onChange={(e) => setStatus(o, e.target.value)}
                data-testid={`order-status-select-${o.code}`}
                className="border border-white/15 bg-obsidian px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke outline-none"
              >
                {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {o.items.map((it, i) => (
              <span key={i} className="border border-white/10 bg-obsidian px-3 py-1.5 font-mono text-[10px] tracking-wider text-smoke">
                {it.name} × {it.quantity}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewsTab({ reviews, reload }) {
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {reviews.length === 0 && <p className="col-span-2 py-16 text-center font-display text-2xl italic text-faint">No words yet.</p>}
      {reviews.map((r) => (
        <div key={r.id} className="flex flex-col border border-white/10 bg-onyx p-6" data-testid={`admin-review-${r.id}`}>
          <div className="flex items-center justify-between">
            <Stars rating={r.rating} size={12} />
            <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${r.approved ? "border-brass/40 text-brass" : "border-vermilion/50 text-vermilion"}`}>
              {r.approved ? "Published" : "Awaiting approval"}
            </span>
          </div>
          <p className="mt-4 flex-1 font-display text-lg font-light italic text-bone">“{r.text}”</p>
          <div className="mt-5 flex items-center justify-between">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-smoke">{r.name}</span>
            <div className="flex gap-2">
              {!r.approved && (
                <button onClick={() => act(() => api.put(`/admin/reviews/${r.id}/approve`, {}, authHeader()), "Review published")} data-testid={`review-approve-${r.id}`} className="flex items-center gap-1.5 border border-brass/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brass transition-colors hover:bg-brass hover:text-obsidian">
                  <Check size={12} /> Approve
                </button>
              )}
              <button onClick={() => act(() => api.delete(`/admin/reviews/${r.id}`, authHeader()), "Review removed")} data-testid={`review-delete-${r.id}`} className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors hover:border-red-500 hover:text-red-400">
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MessagesTab({ messages, reload }) {
  const resolve = async (m) => {
    try {
      await api.put(`/admin/messages/${m.id}/resolve`, {}, authHeader());
      toast.success("Letter answered");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {messages.length === 0 && <p className="py-16 text-center font-display text-2xl italic text-faint">The inbox is silent.</p>}
      {messages.map((m) => (
        <div key={m.id} className={`border p-5 ${m.resolved ? "border-white/5 opacity-50" : "border-white/10 bg-onyx"}`} data-testid={`admin-message-${m.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-light text-bone">{m.subject || "(No subject)"}</p>
              <p className="mt-1 font-mono text-[11px] tracking-wider text-faint">{m.name} — {m.email}</p>
            </div>
            {!m.resolved && (
              <button onClick={() => resolve(m)} data-testid={`message-resolve-${m.id}`} className="flex items-center gap-1.5 border border-brass/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-brass transition-colors hover:bg-brass hover:text-obsidian">
                <Check size={12} /> Mark Resolved
              </button>
            )}
            {m.resolved && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Resolved</span>}
          </div>
          <p className="mt-3 text-sm font-light leading-relaxed text-smoke">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, p, o, r, m] = await Promise.all([
        api.get("/admin/stats", authHeader()),
        api.get("/products"),
        api.get("/admin/orders", authHeader()),
        api.get("/admin/reviews", authHeader()),
        api.get("/admin/messages", authHeader()),
      ]);
      setStats(s.data);
      setProducts(p.data);
      setOrders(o.data);
      setReviews(r.data);
      setMessages(m.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem("an_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(e));
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("an_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const TABS = [
    { id: "products", label: "Objects", icon: Package },
    { id: "orders", label: "Orders", icon: Package },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "messages", label: "Inbox", icon: Package },
  ];

  return (
    <div className="min-h-screen bg-obsidian px-6 pb-24 pt-10 sm:px-12 lg:px-20" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-6">
        <div>
          <p className="eyebrow-label">Atelier Office</p>
          <h1 className="mt-2 font-display text-3xl font-light text-bone sm:text-4xl">Control Room</h1>
        </div>
        <button
          onClick={() => {
            localStorage.removeItem("an_admin_token");
            navigate("/admin/login");
          }}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-xs uppercase tracking-[0.25em] text-smoke transition-colors hover:border-vermilion hover:text-vermilion"
        >
          <LogOut size={14} /> Lock the Office
        </button>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard label="Revenue" value={money(stats.revenue)} testid="stat-revenue" />
          <StatCard label="Orders" value={stats.orders} testid="stat-orders" />
          <StatCard label="Objects" value={stats.products} testid="stat-products" />
          <StatCard label="Open Letters" value={stats.pending_messages} testid="stat-messages" />
          <StatCard label="Reviews Pending" value={stats.pending_reviews} testid="stat-reviews" />
        </div>
      )}

      <div className="mt-10 flex gap-2 border-b border-white/10 pb-0">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`-mb-px border-b-2 px-5 py-3 font-mono text-xs uppercase tracking-[0.25em] transition-colors ${
              tab === t.id ? "border-vermilion text-bone" : "border-transparent text-faint hover:text-smoke"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {!stats && <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-faint">Opening the ledgers…</p>}
        {stats && tab === "products" && <ProductsTab products={products} reload={load} />}
        {stats && tab === "orders" && <OrdersTab orders={orders} reload={load} />}
        {stats && tab === "reviews" && <ReviewsTab reviews={reviews} reload={load} />}
        {stats && tab === "messages" && <MessagesTab messages={messages} reload={load} />}
      </div>
    </div>
  );
}
